import type { GridColDef } from '@mui/x-data-grid'
import { CheckSettingsRow, MultiEditCheckSettingsElement } from '../MultiEditCheckSettings/types'

/**
 * True when the member check settings carry hot dimensioning settings, i.e. the
 * member is timber or timber-slab. Steel columns and "other" members do not.
 */
export const hasHotDimensioningSettings = (
  settings: SettingsOnMember | undefined,
): settings is TimberCheckSettings | TimberSlabCheckSettings =>
  settings != null &&
  (settings.setting_type === 'timber' || settings.setting_type === 'timber-slab')

export const getHotDimensioningSettings = (
  settings: SettingsOnMember | undefined,
): MemberHotDimensioningSettings | undefined =>
  settings && 'hot_dimensioning_settings' in settings
    ? settings.hot_dimensioning_settings
    : undefined

/**
 * Both flags default to off on the backend, so a member that predates the
 * setting is normalized to the same shape the fields expect.
 */
const HOT_DIMENSIONING_SETTINGS_DEFAULT: MemberHotDimensioningSettings = {
  make_hot_dimensioning_checks: false,
}

/**
 * The Heißbemessung form is valid when hot dimensioning is off, or when it is on
 * and a Feuerwiderstandsklasse (fire_duration) is selected. Note F0 maps to 0, so
 * we check against null/undefined rather than falsiness.
 */
export const isHotDimensioningFormValid = (settings: SettingsOnMember | undefined): boolean => {
  const hotDimensioning = getHotDimensioningSettings(settings)
  if (!hotDimensioning?.make_hot_dimensioning_checks) return true
  return hotDimensioning.fire_duration != null
}

/**
 * Ensure the shared settings object always carries a hot_dimensioning_settings
 * object so the field component never reads it as null/undefined.
 */
export const normalizeSettings = (settings: SettingsOnMember): SettingsOnMember =>
  ({
    ...settings,
    hot_dimensioning_settings:
      getHotDimensioningSettings(settings) || HOT_DIMENSIONING_SETTINGS_DEFAULT,
  } as SettingsOnMember)

/**
 * Applies the staged shared hot dimensioning settings to one member, leaving
 * every other part of its check settings untouched.
 */
export const mergeHotDimensioningSettings = (
  existing: SettingsOnMember,
  shared: SettingsOnMember,
): SettingsOnMember =>
  ({
    ...existing,
    hot_dimensioning_settings:
      getHotDimensioningSettings(shared) || HOT_DIMENSIONING_SETTINGS_DEFAULT,
  } as SettingsOnMember)

export const fireDurationToLabel: Record<number, string> = {
  0: 'F0',
  1800: 'F30',
  3600: 'F60',
  5400: 'F90',
  7200: 'F120',
}

/** Short German codes for the fire exposed sides, e.g. "oben, unten". */
export const formatFireExposedSides = (sides: FireExposedSides | undefined): string => {
  if (!sides) return '–'

  const labels = [
    sides.top && 'oben',
    sides.bottom && 'unten',
    sides.left && 'links',
    sides.right && 'rechts',
  ].filter(Boolean)

  return labels.length ? labels.join(', ') : '–'
}

export const hotDimensioningSettingsColumns: GridColDef<CheckSettingsRow>[] = [
  { field: 'active', headerName: 'Heißbemessung', flex: 1, minWidth: 80 },
  { field: 'fireDuration', headerName: 'Feuerwiderstand', flex: 1, minWidth: 80 },
  { field: 'sides', headerName: 'Seiten', flex: 1, minWidth: 100 },
]

/** While a shared value is staged it is previewed for every row. */
export const buildHotDimensioningSettingsRow = (
  element: MultiEditCheckSettingsElement,
  sharedSettings: SettingsOnMember | undefined,
): Record<string, string> => {
  const settings =
    getHotDimensioningSettings(sharedSettings) ?? getHotDimensioningSettings(element.checkSettings)
  const active = !!settings?.make_hot_dimensioning_checks

  return {
    active: active ? 'Ja' : 'Nein',
    fireDuration:
      active && settings?.fire_duration != null
        ? fireDurationToLabel[settings.fire_duration] ?? `${settings.fire_duration}`
        : '–',
    sides: active ? formatFireExposedSides(settings?.fire_exposed_sides) : '–',
  }
}
